import React from "react";
import {Button, Popover, TextField} from "@mui/material";
import type {ComputedOpportunity} from "../briefingLogic";
import {formatAssumptionValue} from "../briefingLogic";
import {useBriefing} from "../briefingState";

type Props = {
    computed: ComputedOpportunity
    anchorEl: HTMLElement | null
    onClose: () => void
};

/**
 * The clock derivation panel ("Why now?"): the numbered chain behind the clock
 * number. Steps that carry an assumption_ref are editable in place — an override
 * requires a reason, recalculates the buffer, reorders the deck and updates the
 * ambient line live.
 */
export const TimingPanel: React.FC<Props> = ({computed, anchorEl, onClose}) => {
    const {state, dispatch} = useBriefing();
    const [editingRef, setEditingRef] = React.useState<string | null>(null);
    const [value, setValue] = React.useState('');
    const [reason, setReason] = React.useState('');

    const opportunity = computed.opportunity;
    const parsed = parseFloat(value);
    const canApply = Number.isFinite(parsed) && parsed > 0 && reason.trim().length > 0;

    const startEdit = (assumptionRef: string, currentValue: string) => {
        setValue(String(parseFloat(currentValue)));
        setReason('');
        setEditingRef(assumptionRef);
    };

    const apply = () => {
        if (!canApply || !editingRef) return;
        dispatch({type: 'setAssumption', id: editingRef, value: parsed, reason: reason.trim()});
        setEditingRef(null);
    };

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            slotProps={{paper: {className: 'db-pop db-timing-pop'}}}
        >
            <div className="db-pop-title">{computed.timingLabel}</div>
            <ol className="db-derivation">
                {computed.derivationSteps.map(step => {
                    const assumptionRef = step.editable ? step.assumption_ref : undefined;
                    return (
                        <li key={step.step} className="db-derivation-step">
                            <span className="db-step-label">{step.label}</span>
                            <span className="db-step-value">
                                {step.value}
                                {assumptionRef && editingRef !== assumptionRef &&
                                    <Button size="small" className="db-step-edit" onClick={() => startEdit(assumptionRef, step.value)}>
                                        Edit
                                    </Button>
                                }
                            </span>
                            {(step.source || step.note) &&
                                <span className="db-step-note">{step.source ?? step.note}</span>
                            }
                            {assumptionRef && editingRef === assumptionRef &&
                                <span className="db-step-editor">
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Days"
                                        value={value}
                                        onChange={e => setValue(e.target.value)}
                                        sx={{width: 90}}
                                    />
                                    <TextField
                                        size="small"
                                        label="Reason (required)"
                                        value={reason}
                                        onChange={e => setReason(e.target.value)}
                                        sx={{flex: 1}}
                                    />
                                    <Button size="small" variant="contained" disableElevation disabled={!canApply} onClick={apply}>
                                        Apply
                                    </Button>
                                    <Button size="small" onClick={() => setEditingRef(null)}>Cancel</Button>
                                </span>
                            }
                        </li>
                    );
                })}
            </ol>
            {opportunity.timing.secondary_timing &&
                <div className="db-pop-note">
                    Renewal timing: expiry {opportunity.timing.secondary_timing.expiry}, latest start {opportunity.timing.secondary_timing.latest_start} — {opportunity.timing.secondary_timing.note}
                </div>
            }
            <div className="db-pop-footer">
                <span className="db-pop-cod">{computed.costDisplay}</span> — {computed.costPlainLanguage}
            </div>
            {(opportunity.assumptions ?? []).filter(a => state.assumptionOverrides[a.id]).map(a => {
                const override = state.assumptionOverrides[a.id];
                return (
                    <div key={a.id} className="db-pop-override">
                        <span>
                            {a.label} overridden ({formatAssumptionValue(a.id, override.value)} — “{override.reason}”).
                            Propagates to: {a.propagates_to}.
                        </span>
                        <Button size="small" onClick={() => dispatch({type: 'resetAssumption', id: a.id})}>
                            Reset to {formatAssumptionValue(a.id, a.value)}
                        </Button>
                    </div>
                );
            })}
        </Popover>
    );
};
